import React from "react";

import { useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button, Col, Form, Input, List, Row, Select, Space, Tag, Typography, Flex, Grid, Popconfirm } from "antd";
import { PlusOutlined, MinusOutlined} from "@ant-design/icons";

import { PropriedadesDoPopConfirm } from "../../../Defaults";
import { EnumPessoaTipo } from "../../../Models/Pessoa/Pessoa";
import { Permissoes } from "../../../Constants/Permissoes";
import { EmpresaService } from '../../../Services/Pessoa/EmpresaService';
import { PlanoContasService } from "../../../Services/Financeiro/PlanoContasService";
import { PlanoContasTipoService } from "../../../Services/Financeiro/PlanoContasTipoService";
import { Restrito } from "../../../Components/Restrito";
import { SelectComPesquisa } from "../../../Components/SelectComPesquisa";
import { InputComReactIMask } from "../../../Components/InputComReactIMask";
import { SessaoContext } from "../../../Contexts/SessaoContext";
import { TypeEmpresa } from "../../../Models/Pessoa/Empresa";
import { TypePlanoContas, formatarCodigoContabil } from "../../../Models/Financeiro/PlanoContas";
import { TypePlanoContasTipo } from "../../../Models/Financeiro/PlanoContasTipo";
import { TypeRegistro } from "../../../Models/Financeiro/PlanoContasTipoRegistro";

import { Link } from "react-router-dom";

import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../Components/Conjunto";
import { Ordenar } from "../../../Utils/Ordenacao";
import { Formatacoes } from "../../../Utils/Formatacao";

import * as Tutoriais from "./listagemDePlanoDeContas.tutorial"
import AdicionarAjuda, { ITutorial } from "../../../Components/AdicionarAjuda";
import { EnumRegistroTipo } from "../../../Models/Financeiro/PlanoContasTipoRegistro";

export const ListagemDePlanosDeContas: React.FC<{
    aoEditar?: (item: TypePlanoContas ) => any,
    aoAdicionar?: () => any,

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (grupo: TypePlanoContas) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();

    const [filtro, setFiltro] = useState<TypePlanoContas>({
        ativo : true
    });

    const planoContasService = new PlanoContasService();
    const planoContasTipoService = new PlanoContasTipoService();
    const empresaService = new EmpresaService();

    const listaEmpresas = useQuery({
        queryKey: ['listarEmpresas'],
        queryFn: () => empresaService.listar({ tipo: EnumPessoaTipo.EMPRESA, ativo: true }),
        enabled: true,
    });

    const listarTipo = useQuery({
        queryKey: ['listarTipo'],
        queryFn: () => planoContasTipoService.listarTipo({})
    });

    const listarTipoClassificacao = useQuery({
        queryKey: ['listarTipoClassificacao'],
        queryFn: () => planoContasTipoService.listarClassificacao({})
    });

    const listaPlanoContas = useQuery({
        queryKey: ['listarPlano', filtro],
        queryFn: () => planoContasService.listar(filtro),
        gcTime: 0,
        enabled: true
    });

    interface IFormFiltro {
        ddlEmpresa: TypeEmpresa["codigo"],
        txtDescricao: string,
        txtCodigoContabil: string,

        ddlTipo: TypePlanoContasTipo["codigo"],
        ddlClassificacao: TypeRegistro["codigo"],
        ddlAtivo: boolean
    }

    const filtrarTabela = (values: IFormFiltro) => {
        const planoContasFiltro: TypePlanoContas = {
            empresa: {
                codigo: values.ddlEmpresa 
            },
            descricao: values.txtDescricao,
            codigoContabil: values.txtCodigoContabil,
            tipo: {
                codigo: values.ddlTipo 
            },
            tipoRegistro: {
                codigo: values.ddlClassificacao 
            },
            ativo: values.ddlAtivo 
        }

        setFiltro(planoContasFiltro);
    }

    const [chavesDasLinhasExpandidas, setChavesDasLinhasExpandidas] = useState<React.Key[]>([])
    const adicionar = permissoes?.includes(Permissoes.CriarPlanoDeContas);

    return (
        <>
            <Form<IFormFiltro> name="listar" onFinish={filtrarTabela} layout="vertical" autoComplete="off">
                <Row id={Tutoriais.IDs.formularioDeFiltro} gutter={16}>
                    <Col xs={24} md={4} id={Tutoriais.IDs.filtroDeEmpresa}>
                        <Form.Item<IFormFiltro> name="ddlEmpresa" label="Empresa">
                            <SelectComPesquisa
                                allowClear
                                placeholder={"Todos"}
                                options={(listaEmpresas.data?.data || []).map((x) => ({
                                    value: x.codigo,
                                    label: x.nome!,
                                }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8} id={Tutoriais.IDs.filtroDeDescricao}>
                        <Form.Item<IFormFiltro> name="txtDescricao" label="Descrição">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={3} id={Tutoriais.IDs.filtroDeTipo}>
                        <Form.Item<IFormFiltro> name="ddlTipo" label="Tipo">
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={listarTipo.data?.data.map((x) => ({
                                    value: x.codigo,
                                    label: x.tipo,
                                }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={3} id={Tutoriais.IDs.filtroDeClassificacao}>
                        <Form.Item<IFormFiltro> name="ddlClassificacao" label="Classificação">
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={listarTipoClassificacao.data?.data.map((x) => ({
                                    value: x.codigo,
                                    label: x.tipo,
                                }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={4} id={Tutoriais.IDs.filtroDeCodigoContabil}>
                        <Form.Item<IFormFiltro> shouldUpdate={(anterior, atual) => anterior?.ddlClassificacao != atual?.ddlClassificacao}>
                            {
                                ({getFieldValue}) => {
                                    const tipoRegistro = getFieldValue([ "ddlClassificacao" ]);
                                    return <Form.Item<IFormFiltro>
                                        name={["txtCodigoContabil"]}
                                        rules={[{ max: 6 }]}
                                        normalize={Formatacoes.somenteDigitos}
                                        label={"Código contábil"}
                                    >
                                        <Input placeholder="Código contábil" />
                                    </Form.Item>;
                                }
                            }
                        </Form.Item>
                    </Col>
                    
                    <Col xs={24} md={3} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFormFiltro> name="ddlAtivo" label="Ativo" initialValue={true}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={8}>
                    <Col xs={adicionar ? 12 : 24} md={adicionar ? 2 : 5} style={{ alignItems: "end", display: "flex" }}>
                        <Form.Item style={{ flexGrow: 1 }}>
                            <Button
                                type="primary"
                                key="submit"
                                htmlType="submit"
                                id={Tutoriais.IDs.botaoListar}
                                style={{ width: "100%" }}
                                disabled={listaPlanoContas.isFetching}
                            >
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>

                    <Restrito aberto={adicionar}>
                        <Col xs={12} md={3} style={{ alignItems: "end", display: "flex" }}>
                            <Form.Item style={{ flexGrow: 1 }}>
                                <Link
                                    to={props.hrefDoBotaoAdicionar ?? "#"}
                                    onClick={(e) => {
                                        !props.hrefDoBotaoAdicionar && e.preventDefault();
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            props.aoAdicionar?.();
                                        }}
                                        style={{ width: "100%" }}
                                    >
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
            </Form>

            <Row>
                <Col xs={24}>
                    <ResponsiveAntdTable<TypePlanoContas>
                        bordered
                        locale={{ emptyText: "Nenhum plano de contas encontrado." }}
                        dataSource={listaPlanoContas.data?.data}
                        loading={listaPlanoContas.isFetching}
                        rowKey={(k) => k.codigo!}
                        addTitleOn="lg"
                        expandable={{
                            showExpandColumn: false,
                            expandedRowRender: (record) => (
                                <div>
                                    <ResponsiveAntdTable<Partial<TypePlanoContas>>
                                        columns={[
                                            {
                                                title: "Empresa",
                                                breakpoints: { xs: 24, sm : 8 },
                                                dataIndex: ["empresa", "nome"],
                                                render: (v) => v ?? "N/A",
                                                key: "empresa-nome",
                                            },
                                            {
                                                title: "Tipo",
                                                breakpoints: { xs: 24, sm : 8 },
                                                dataIndex: ["tipo", "tipo"],
                                                sorter: (a, b) => Ordenar.String(a.tipo?.tipo, b.tipo?.tipo),
                                                key: "plano-contas-tipo",
                                            },
                                            {
                                                title: "Classificação",
                                                breakpoints: { xs: 24, sm : 8 },
                                                dataIndex: ["tipoRegistro", "tipo"],
                                                sorter: (a, b) => Ordenar.String(a.tipoRegistro?.tipo, b.tipoRegistro?.tipo),
                                                key: "plano-contas-classificacao",
                                            }
                                        ]}
                                        dataSource={[
                                            {
                                                empresa: record.empresa,
                                                tipo: record.tipo,
                                                tipoRegistro: record.tipoRegistro,
                                            },
                                        ]}
                                    />
                                </div>
                            ),
                            rowExpandable: () => true,
                            expandedRowKeys: chavesDasLinhasExpandidas,
                        }}
                        columns={[
                            {
                                title: "Nº G",
                                breakpoints: { xs : 6, sm : 6, md : 4, lg : 3, xl : 2, xxl: 2 },
                                dataIndex: ["grupo", "codigoContabil"],
                                key: "grupoCodigoContabil",
                                render: (v: string, record:TypePlanoContas) => {
                                    const valor = record.tipoRegistro?.codigo === EnumRegistroTipo.Grupo ? record.codigoContabil : formatarCodigoContabil(record.grupo ?? {});
                                    return (valor ?? "").trim().length === 0 ? " - " : (valor ?? " - ");
                                },
                            },
                            {
                                title: "Grupo",
                                breakpoints: { xs : 18, sm : 18, md : 8, lg : 5, xl : 4, xxl: 4 },
                                dataIndex: ["grupo", "descricao"],
                                key: "grupo",
                                render: (v: string, record:TypePlanoContas) => {
                                    const valor = record.tipoRegistro?.codigo === EnumRegistroTipo.Grupo ? record.descricao : record.grupo?.descricao;
                                    return (valor ?? "").trim().length === 0 ? " - " : (valor ?? " - ");
                                },
                                },
                            {
                                title: "Nº S",
                                breakpoints: { xs : 6, sm : 6, md : 4, lg : 3, xl : 2, xxl: 2 },
                                dataIndex: ["subGrupo", "codigoContabil"],
                                key: "subGrupoCodigoContabil",
                                render: (v: string, record:TypePlanoContas) => {
                                    const valor = record.tipoRegistro?.codigo === EnumRegistroTipo.Subgrupo ? formatarCodigoContabil(record) : formatarCodigoContabil({
                                        ...record.subGrupo,
                                        grupo : record.grupo
                                    });
                                    return (valor ?? "").trim().length === 0 ? " - " : (valor ?? " - ");
                                },
                            },
                            {
                                title: "Subgrupo",
                                breakpoints: { xs : 18, sm : 18, md : 8, lg : 5, xl : 4, xxl: 4 },
                                dataIndex: ["subGrupo", "descricao"],
                                key: "subgrupo",
                                render: (v: string, record:TypePlanoContas) => {
                                    const valor = record.tipoRegistro?.codigo === EnumRegistroTipo.Subgrupo ? record.descricao : record.subGrupo?.descricao;
                                    return (valor ?? "").trim().length === 0 ? " - " : (valor ?? " - ");
                                },
                            },
                            {
                                title: "Código",
                                breakpoints: { xs : 6, sm : 6, md : 4, lg : 3, xl : 2, xxl: 2 },
                                dataIndex: ["codigoContabil"],
                                sorter: (a, b) => Ordenar.String(a.codigoContabil, b.codigoContabil),
                                render: (v: string, record:TypePlanoContas) => {
                                    const valor = record.tipoRegistro?.codigo === EnumRegistroTipo.Item ? formatarCodigoContabil(record) : null;
                                    return (valor ?? "").trim().length === 0 ? " - " : (valor ?? " - ");
                                },
                                key: "plano-de-contas-codigo-contabil",
                            },
                            {
                                title: "Descrição",
                                breakpoints: { xs : 18, sm : 18, md : 8, lg : 5, xl : 4, xxl: 4 },
                                dataIndex: ["descricao"],
                                sorter: (a, b) => Ordenar.String(a.descricao, b.descricao),
                                key: "plano-contas-descricao",
                                render: (v: string, record:TypePlanoContas) => {
                                    const valor = record.tipoRegistro?.codigo === EnumRegistroTipo.Item ? record.descricao : null;
                                    return (valor ?? "").trim().length === 0 ? " - " : (valor ?? " - ");
                                },
                            },
                            {
                                title: "Ativo",
                                dataIndex: ["ativo"],
                                breakpoints: { xs : 6, sm : 6, md : 4, lg : 3, xl : 2, xxl: 2 },
                                sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                key: "plano-contas-ativo",
                                render: (v: boolean) => {
                                    return v ? "Sim" : "Não";
                                },
                            },
                            {
                                title: "Ações",
                                key: "plano-contas-acoes",
                                noResponsiveTitle: true,
                                breakpoints: { xs : 18, sm : 18, md : 8, lg : 21, xl : 4, xxl: 4 },
                                hidden: [Permissoes.AtualizarPlanoDeContas, Permissoes.DesativarPlanoDeContas, Permissoes.AtivarPlanoDeContas].every(
                                    (p) => !permissoes?.includes(p)
                                ),
                                render: (_, item) => {
                                    return (
                                        <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: "wrap" }}
                                            itens={[
                                                <Button
                                                    type="dashed"
                                                    size="small"
                                                    icon={item.codigo && chavesDasLinhasExpandidas.includes(item.codigo) ? <MinusOutlined /> : <PlusOutlined />}
                                                    onClick={() => {
                                                        if (!item.codigo) return;

                                                        if (chavesDasLinhasExpandidas.includes(item.codigo)) {
                                                            setChavesDasLinhasExpandidas((prev) => prev.filter((v) => v != item.codigo));
                                                        } else {
                                                            setChavesDasLinhasExpandidas((prev) => [...prev, item.codigo!]);
                                                        }
                                                    }}
                                                ></Button>,
                                                ...(() => {
                                                    if (item.ativo) {
                                                        return [
                                                            permissoes?.includes(Permissoes.AtualizarPlanoDeContas) && (
                                                                <Link
                                                                    to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                    onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                                >
                                                                    <Button
                                                                        className={Tutoriais.Classes.botaoEditar}
                                                                        type="primary"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            props.aoEditar?.(item);
                                                                        }}
                                                                    >
                                                                        Editar
                                                                    </Button>
                                                                </Link>
                                                            ),
                                                            permissoes?.includes(Permissoes.DesativarPlanoDeContas) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("desativar")}
                                                                    onConfirm={() => {
                                                                        if (!item.codigo) return;
                                                                        planoContasService
                                                                            .desativar(item.codigo)
                                                                            .then(() => {
                                                                                listaPlanoContas.refetch();
                                                                            })
                                                                            .catch(() => []);
                                                                    }}
                                                                >
                                                                    <Button type="default" danger size="small" className={Tutoriais.Classes.botaoDesativar}>
                                                                        Desativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            ),
                                                        ];
                                                    } else {
                                                        return [
                                                            permissoes?.includes(Permissoes.AtivarPlanoDeContas) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("ativar")}
                                                                    onConfirm={() => {
                                                                        if (!item.codigo) return;
                                                                        planoContasService
                                                                            .ativar(item.codigo)
                                                                            .then(() => {
                                                                                listaPlanoContas.refetch();
                                                                            })
                                                                            .catch(() => {});
                                                                    }}
                                                                >
                                                                    <Button type="dashed" size="small">
                                                                        Reativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            ),
                                                        ];
                                                    }
                                                })(),
                                            ]}
                                        />
                                    );
                                },
                            },
                        ]}
                        csv={{
                            enabled: true,
                            filename: "planos-de-contas.csv",
                            options: {
                                emptyFieldValue: "-"
                            }

                            // concat: (a) => {
                            //     return [
                            //         {
                            //             header: "Código contábil",
                            //             data: a.map(m => [m.grupo?.codigoContabil, m.subGrupo?.codigoContabil, m.codigoContabil].filter(f => (f?.length ?? 0) > 0).join(".")),
                            //         },
                            //     ];
                            //   }
                        }}
                    />
                </Col>
            </Row>

            <AdicionarAjuda
                tutoriais={
                    [
                        permissoes?.includes(Permissoes.ListarPlanosDeContas) && {
                            titulo: "Como consultar os planos de contas?",
                            tutorial: Tutoriais.ConsultarPlano,
                        },

                        permissoes?.includes(Permissoes.CriarPlanoDeContas) && {
                            titulo: "Como adicionar um plano de contas?",
                            tutorial: Tutoriais.AdicionarPlano,
                        },

                        permissoes?.includes(Permissoes.AtualizarPlanoDeContas) && {
                            titulo: "Como editar um plano de contas?",
                            tutorial: Tutoriais.EditarPlano,
                        },

                        permissoes?.includes(Permissoes.DesativarPlanoDeContas) && {
                            titulo: "Como desativar um plano de contas?",
                            tutorial: Tutoriais.DesativarPlano,
                        },
                    ].filter((v) => v != false) as ITutorial[]
                }
            />
        </>
    );
};
